import {
  INIT_POSTS_PAGE_ROUTINE,
  INIT_SELECTED_ACCOUNTS,
  SET_SELECTED_ACCOUNTS,
  SKED_CORE_GET_ACCOUNTS_ROUTINE,
  UPDATE_ACCOUNT,
  UPDATE_ACCOUNTS_COUNT,
  UPDATE_SELECTED_ACCOUNTS,
} from "constants/ActionTypes";
import { createReducer } from "@reduxjs/toolkit";
import { normalizeEntity } from "reducers/reducerUtils";
import isEqual from "lodash/isEqual";
import { adminPanelFeatureToggle } from "utils/feature-toggle-utils";
import {
  addAccount,
  addAccounts,
  removeAccount,
  replaceAccount,
  replaceAllAccounts,
} from "@/entities/accounts/table";

const initialState = {
  byId: {},
  allIds: [],
  selectedIds: [],
  withToken: [],
  /**
   * Tracks the fetch status of accounts:
   * - 'idle': Initial state, no fetch attempted yet
   * - 'loading': Fetch in progress
   * - 'succeeded': Fetch completed successfully
   * - 'failed': Fetch failed
   *
   * This is used by getAccountsReady selector to distinguish between
   * "accounts loaded successfully" vs "accounts fetch failed".
   */
  /** @type {"idle"} */
  fetchStatus: "idle",
  counts: {
    fbGroupCount: 0,
    fbPageCount: 0,
    gmbCount: 0,
    igAllowed: 0,
    igCount: 0,
    igFollowers: 0,
    liCount: 0,
    otherAllowed: 0,
    otherCount: 0,
    piCount: 0,
    twCount: 0,
    ttCount: 0,
    usesSkedLink: false,
    ytCount: 0,
    notificationCount: 0,
    notificationAllowed: 0,
    maxAllowed: 0,
    totalCount: 0,
    pricingVersion: "",
  },
};

/**
 * @param {"succeeded" | undefined} fetchStatus
 */
const loadAccounts = (state, action, fetchStatus = undefined) => {
  const { accounts } = action.payload;

  if (!accounts) {
    return fetchStatus ? { ...state, fetchStatus } : state;
  }

  adminPanelFeatureToggle.notifyFromAccounts(accounts);

  const withToken = accounts.filter((a) => {
    return !!a.igTokenAddedAt;
  });

  const { byId, allIds } = normalizeEntity(accounts);

  return {
    ...state,
    byId,
    allIds,
    withToken,
    ...(fetchStatus && { fetchStatus }),
  };
};

const getAccountsList = (state) =>
  state.allIds.map((accountId) => state.byId[accountId]).filter(Boolean);

const syncWithToken = (state) => {
  state.withToken = getAccountsList(state).filter((account) => {
    return !!account.igTokenAddedAt;
  });
};

const accountsReducer = createReducer(initialState, (builder) => {
  builder.addCase(replaceAllAccounts, (state, action) => {
    state.byId = action.payload.byId;
    state.allIds = action.payload.ids;
    syncWithToken(state);
    return state;
  });
  builder.addCase(addAccount, (state, action) => {
    const account = action.payload;

    if (!state.byId[account._id]) {
      state.allIds.push(account._id);
    }

    state.byId[account._id] = account;
    syncWithToken(state);
    return state;
  });
  builder.addCase(addAccounts, (state, action) => {
    for (const account of action.payload) {
      if (!state.byId[account._id]) {
        state.allIds.push(account._id);
      }

      state.byId[account._id] = account;
    }
    syncWithToken(state);
    return state;
  });
  builder.addCase(replaceAccount, (state, action) => {
    const account = action.payload;

    if (!state.byId[account._id]) {
      state.allIds.push(account._id);
    }

    state.byId[account._id] = account;
    syncWithToken(state);
    return state;
  });
  builder.addCase(removeAccount, (state, action) => {
    const accountId = action.payload;

    if (!state.byId[accountId]) {
      return state;
    }

    delete state.byId[accountId];
    state.allIds = state.allIds.filter((id) => id !== accountId);
    state.selectedIds = state.selectedIds.filter((id) => id !== accountId);
    syncWithToken(state);
    return state;
  });
  builder.addCase(INIT_SELECTED_ACCOUNTS, (state, action) => {
    const { selectedAccountIds } = action.payload;
    state.selectedIds = selectedAccountIds;
    return state;
  });
  builder.addCase(SET_SELECTED_ACCOUNTS, (state, action) => {
    const { selectedAccountIds } = action.payload;
    state.selectedIds = selectedAccountIds;
    return state;
  });
  builder.addCase(SKED_CORE_GET_ACCOUNTS_ROUTINE.REQUEST, (state) => {
    state.fetchStatus = "loading";
    return state;
  });
  builder.addCase(SKED_CORE_GET_ACCOUNTS_ROUTINE.SUCCESS, (state, action) => {
    // Pass fetchStatus to loadAccounts to avoid Immer error
    // (can't both mutate draft and return new value)
    return loadAccounts(state, action, "succeeded");
  });
  builder.addCase(SKED_CORE_GET_ACCOUNTS_ROUTINE.FAILURE, (state) => {
    state.fetchStatus = "failed";
    state.byId = {};
    state.allIds = [];
    state.selectedIds = [];
    return state;
  });
  builder.addCase(INIT_POSTS_PAGE_ROUTINE.TRIGGER, (state) => {
    // const { selectedAccountIds } = action.payload;
    // if (_.isEqual(selectedAccountIds, state.selectedIds)) {
    //   return state;
    // }
    //
    // state.selectedIds = selectedAccountIds;
    return state;
  });
  builder.addCase(UPDATE_SELECTED_ACCOUNTS, (state, action) => {
    const { selectedAccountIds } = action.payload;
    if (isEqual(selectedAccountIds, state.selectedIds)) {
      return state;
    }

    state.selectedIds = selectedAccountIds;
    return state;
  });
  builder.addCase(UPDATE_ACCOUNT, (state, action) => {
    const { accountId, account } = action.payload;
    const targetAccount = state.byId[accountId];

    if (!targetAccount) {
      return;
    }

    state.byId[accountId] = {
      ...targetAccount,
      ...account,
    };
    syncWithToken(state);
    return state;
  });
  builder.addCase(UPDATE_ACCOUNTS_COUNT, (state, action) => {
    return {
      ...state,
      counts: {
        ...state.counts,
        ...action.payload.counts,
      },
    };
  });
});

export default accountsReducer;
